'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../utils/cn';

const buttonVariants = cva(
  [
    // Base styles with liquid glass design - more rounded and smooth
    'inline-flex items-center justify-center whitespace-nowrap rounded-xl font-medium transition-all duration-300 ease-out cursor-pointer',
    // Focus states using design system with softer ring
    'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
    // Disabled states
    'disabled:pointer-events-none disabled:opacity-50',
    // Active state - subtle scale interaction
    'active:scale-[0.98]',
  ],
  {
    variants: {
      variant: {
        // Default/Neutral button with glass effect
        default: [
          'glass-surface text-foreground border border-border/50 shadow-sm',
          'hover:glass-surface-hover hover:shadow-md hover:border-border hover:scale-[1.01]',
        ],
        // Primary button with glass effect
        primary: ['glass-button text-white', 'hover:shadow-lg hover:scale-[1.02]'],
        // Destructive - liquid glass design in red (like primary but red)
        destructive: ['glass-button-destructive text-white', 'hover:shadow-lg hover:scale-[1.02]'],
        // Outline variant with glass effect
        outline: [
          'glass-surface text-foreground border-2 border-border shadow-sm',
          'hover:glass-surface-hover hover:shadow-md hover:border-primary/30 hover:scale-[1.01]',
        ],
        // Secondary variant with glass effect
        secondary: [
          'glass-button-secondary text-foreground border border-border/30 shadow-sm',
          'hover:glass-surface-hover hover:shadow-md hover:border-border/50 hover:scale-[1.01]',
        ],
        // Ghost variant for minimal styling with subtle glass on hover
        ghost: ['text-foreground', 'hover:glass-surface hover:shadow-sm hover:scale-[1.01]'],
        // Link variant
        link: ['text-primary underline-offset-4', 'hover:underline hover:text-primary-600'],
      },
      size: {
        // Sizes based on Figma Space tokens and typography - balanced outer padding and icon-text gap
        sm: 'h-8 px-4 text-xs font-medium gap-1.5', // 32px height
        md: 'h-10 px-5 py-2 text-sm font-medium gap-2', // 40px height
        lg: 'h-12 px-7 text-base font-medium gap-2', // 48px height
        xl: 'h-14 px-9 text-lg font-medium gap-2', // 56px height
        icon: 'h-10 w-10', // Square button for icons
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export type NonClickableButtonProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onClick' | 'onMouseDown' | 'onMouseUp' | 'onKeyDown' | 'onKeyUp'
> &
  VariantProps<typeof buttonVariants>;

const NonClickableButton = React.forwardRef<HTMLDivElement, NonClickableButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <div
        className={cn(
          buttonVariants({ variant, size }),
          // Remove interactive states and cursor
          'cursor-default pointer-events-none select-none',
          // Remove focus and active styles by overriding them
          'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0',
          'active:brightness-100',
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);
NonClickableButton.displayName = 'NonClickableButton';

export { Button, NonClickableButton, buttonVariants };
